/**
 * Calculator - Assembled calculator component
 *
 * Combines:
 * - CalculatorFrame (chrome carcass)
 * - CalculatorDisplay (LCD display)
 * - CalculatorButton (button pad)
 */

import { cn } from '@/lib/utils';
import { CalculatorFrame } from './CalculatorFrame';
import { CalculatorDisplay } from './CalculatorDisplay';
import { CalculatorButton, ButtonType } from './CalculatorButton';

// ============================================
// TYPES
// ============================================

export interface CalculatorProps {
  /** Current display value */
  value?: string;
  /** Maximum digits to display */
  maxDigits?: number;
  /** Minimum amount for OK to be enabled */
  minAmount?: number;
  /** Width of calculator */
  width?: number;
  /** Height of calculator */
  height?: number;
  /** Additional class names */
  className?: string;
  /** Button press handler */
  onButtonPress?: (button: ButtonType) => void;
  /** Back/exit handler (when DEL pressed with empty value) */
  onBack?: () => void;
  /** OK/confirm handler */
  onConfirm?: (value: string) => void;
}

// ============================================
// COMPONENT
// ============================================

export function Calculator({
  value = '',
  maxDigits = 6,
  minAmount = 10,
  width = 280,
  height = 416,
  className,
  onButtonPress,
  onBack,
  onConfirm,
}: CalculatorProps) {
  const numericValue = parseInt(value) || 0;
  const isOkEnabled = numericValue >= minAmount;
  const isValueEmpty = value === '' || value === '0';

  const handlePress = (type: ButtonType) => {
    onButtonPress?.(type);

    if (type === 'del' && isValueEmpty) {
      onBack?.();
    } else if (type === 'ok' && isOkEnabled) {
      onConfirm?.(value);
    }
  };

  return (
    <div className={cn('calculator', className)}>
      <CalculatorFrame
        width={width}
        height={height}
        displayContent={
          <CalculatorDisplay value={value} maxDigits={maxDigits} />
        }
        buttonContent={
          <div className="calculator__buttons">
            <CalculatorButton type="1" onPress={handlePress} />
            <CalculatorButton type="2" onPress={handlePress} />
            <CalculatorButton type="3" onPress={handlePress} />
            <CalculatorButton type="4" onPress={handlePress} />
            <CalculatorButton type="5" onPress={handlePress} />
            <CalculatorButton type="6" onPress={handlePress} />
            <CalculatorButton type="7" onPress={handlePress} />
            <CalculatorButton type="8" onPress={handlePress} />
            <CalculatorButton type="9" onPress={handlePress} />
            <CalculatorButton type="del" onPress={handlePress} />
            <CalculatorButton type="0" onPress={handlePress} />
            <CalculatorButton type="ok" onPress={handlePress} disabled={!isOkEnabled} />
          </div>
        }
      />
    </div>
  );
}
